import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Lines } from "./Reveal";
import { LayersDiagram } from "./diagrams/LayersDiagram";
import { ChemistryDiagram } from "./diagrams/ChemistryDiagram";
import { DiffusionDiagram } from "./diagrams/DiffusionDiagram";
import { ColourDiagram } from "./diagrams/ColourDiagram";
import { ListDiagram } from "./diagrams/ListDiagram";

const SAFETY = [
  ["Food-safe materials", "All layers use FDA/EFSA-compliant, food-grade materials."],
  ["No leakage", "The indicator dye is fully encapsulated. It never touches the food."],
  ["Breathable but selective", "The membrane lets small volatile molecules pass while blocking larger molecules and liquids."],
  ["Compatible with existing packaging", "Applied as a sticker, or integrated into packaging films and labels."],
];

const VALIDATION = [
  ["Sensitivity", "Detects spoilage at the right level."],
  ["Selectivity", "Not triggered by normal food compounds."],
  ["Response time", "How quickly the colour changes."],
  ["Stability", "Works across temperature and humidity ranges."],
  ["Food safety", "No contamination, no leakage."],
  ["Real-world performance", "Tested in actual packaged food."],
];

const TOPICS = [
  { n: "01", title: "The layer structure", lead: "Four functional layers, each with a defined role, plus a release liner removed at application.", view: () => <LayersDiagram /> },
  { n: "02", title: "The chemistry", lead: "Microbial spoilage releases volatile basic compounds; a pH-sensitive dye registers the resulting shift.", view: () => <ChemistryDiagram /> },
  { n: "03", title: "How the gas diffuses", lead: "A semi-permeable membrane admits small volatile molecules while retaining the dye.", view: () => <DiffusionDiagram /> },
  { n: "04", title: "The colour change", lead: "As the concentration of spoilage compounds rises, the indicator transitions progressively from purple to yellow.", view: () => <ColourDiagram /> },
  { n: "05", title: "Materials and safety", lead: "Food-grade materials; the indicator chemistry is fully encapsulated and never contacts the product.", view: () => <ListDiagram items={SAFETY} testId="safety" /> },
  { n: "06", title: "Validation before market", lead: "Six performance criteria the indicator must satisfy before commercial release.", view: () => <ListDiagram items={VALIDATION} testId="validation" /> },
];

export const Science = () => {
  const [active, setActive] = useState(0);
  const t = TOPICS[active];

  useEffect(() => {
    const onTopic = (e) => setActive(e.detail);
    window.addEventListener("telltag:topic", onTopic);
    return () => window.removeEventListener("telltag:topic", onTopic);
  }, []);

  return (
    <section id="science" data-testid="science-section" className="bg-[#F2F3F5] text-[#0D1B2A] py-28 md:py-40 px-6 md:px-10">
      <div className="grid grid-cols-12 gap-x-6 gap-y-12">
        <div className="col-span-12 md:col-span-8">
          <p className="eyebrow text-[#595959] mb-6">How the indicator works</p>
          <Lines lines={["A measurement,", "not an estimate."]} className="display text-[2.4rem] sm:text-6xl lg:text-[5rem]" testId="science-headline" />
        </div>
      </div>

      <div className="grid grid-cols-12 gap-x-6 gap-y-10 mt-16 md:mt-24">
        <ol className="col-span-12 md:col-span-4 lg:col-span-3" data-testid="science-topics">
          {TOPICS.map((tp, i) => (
            <li key={tp.n} className="border-t border-[#0D1B2A]/15">
              <button
                type="button"
                onClick={() => setActive(i)}
                aria-pressed={active === i}
                className="w-full text-left py-4 md:py-5 grid grid-cols-[2.5rem_1fr] gap-3 items-baseline transition-opacity duration-300"
                style={{ opacity: active === i ? 1 : 0.5 }}
                data-testid={`science-topic-${tp.n}`}
              >
                <span className="mono text-xs">{tp.n}</span>
                <span className="text-base md:text-lg font-medium tracking-tight">{tp.title}</span>
              </button>
            </li>
          ))}
          <li className="border-t border-[#0D1B2A]/15" />
        </ol>

        <div className="col-span-12 md:col-span-8 md:col-start-5 lg:col-span-8 lg:col-start-5">
          <AnimatePresence mode="wait">
            <motion.div
              key={t.n}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              data-testid={`science-panel-${t.n}`}
            >
              <p className="body-copy max-w-[52ch] mb-8">{t.lead}</p>
              {t.view()}
              <p className="mono text-[0.65rem] tracking-[0.15em] uppercase text-[#595959] mt-6">Tap any element for detail</p>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};
